import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  DocumentTextIcon,
  ClockIcon,
  CurrencyDollarIcon,
  FolderIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { DateInput } from '@/components/ui/DateInput';
import { DateTimeInput } from '@/components/ui/DateTimeInput';
import { cn } from '@/utils/cn';
import toast from 'react-hot-toast';
import {
  legalService,
  LegalMatter,
  MatterEvent,
  MatterCost,
  MatterDocument,
  FinancialSummary,
  EVENT_TYPES,
  COST_CATEGORIES,
  EVENT_TYPE_LABELS,
  COST_CATEGORY_LABELS,
  MATTER_TYPE_LABELS,
  MATTER_STATUS_LABELS,
} from '../services/legal.service';

// ── Types ────────────────────────────────────────────────────────────────────

type TabKey = 'overview' | 'timeline' | 'financial' | 'documents' | 'holds';

// ── Helpers ────────────────────────────────────────────────────────────────────

const STATUS_BADGE_COLORS: Record<string, string> = {
  success: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
  warning: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
  danger: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
  secondary: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
  primary: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300',
};

const inputCls =
  'w-full rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 px-3 py-2 text-sm text-secondary-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500';
const labelCls = 'text-sm font-medium text-secondary-700 dark:text-secondary-300';

// ── Sub-components ────────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1">
      <span className="text-sm text-secondary-500 dark:text-secondary-400 sm:w-40 flex-shrink-0">
        {label}
      </span>
      <span className="text-sm font-medium text-secondary-900 dark:text-white">{value ?? '—'}</span>
    </div>
  );
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

function OverviewTab({ matter }: { matter: LegalMatter }) {
  return (
    <div className="space-y-6">
      {matter.summary && (
        <Card>
          <CardHeader>
            <CardTitle>Summary</CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            <div className="text-sm text-secondary-700 dark:text-secondary-300 prose prose-sm dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: matter.summary }} />
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Key Dates */}
        <Card>
          <CardHeader>
            <CardTitle>Key Dates</CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6 space-y-3">
            <InfoRow label="Date Opened" value={legalService.formatDate(matter.dateOpened)} />
            <InfoRow
              label="Date of Incident"
              value={legalService.formatDate(matter.dateOfIncident)}
            />
            <InfoRow
              label="Statute of Limitations"
              value={legalService.formatDate(matter.statuteOfLimitations)}
            />
            <InfoRow label="Next Key Date" value={legalService.formatDate(matter.nextKeyDate)} />
            <InfoRow
              label="Expected Resolution"
              value={legalService.formatDate(matter.expectedResolution)}
            />
            {matter.dateClosed && (
              <InfoRow label="Date Closed" value={legalService.formatDate(matter.dateClosed)} />
            )}
          </CardContent>
        </Card>

        {/* Parties */}
        <Card>
          <CardHeader>
            <CardTitle>Parties</CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6 space-y-3">
            <InfoRow label="Lead Attorney" value={matter.leadAttorneyName} />
            <InfoRow
              label="Supporting Counsel"
              value={
                matter.supportingCounsel?.length
                  ? matter.supportingCounsel.join(', ')
                  : '—'
              }
            />
            <InfoRow
              label="Paralegals"
              value={
                matter.assignedParalegals?.length
                  ? matter.assignedParalegals.join(', ')
                  : '—'
              }
            />
            <InfoRow
              label="Stakeholders"
              value={
                matter.businessStakeholders?.length
                  ? matter.businessStakeholders.join(', ')
                  : '—'
              }
            />
          </CardContent>
        </Card>

        {/* External Counsel */}
        {matter.externalCounsel?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>External Counsel</CardTitle>
            </CardHeader>
            <CardContent className="px-6 pb-6">
              <ul className="space-y-1">
                {matter.externalCounsel.map((ec, i) => (
                  <li key={i} className="text-sm text-secondary-700 dark:text-secondary-300">
                    {ec}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Court Info */}
        {matter.courtInfo && (
          <Card>
            <CardHeader>
              <CardTitle>Court Information</CardTitle>
            </CardHeader>
            <CardContent className="px-6 pb-6 space-y-3">
              {Object.entries(matter.courtInfo).map(([k, v]) => (
                <InfoRow key={k} label={k} value={String(v)} />
              ))}
            </CardContent>
          </Card>
        )}

        {/* Budget */}
        <Card>
          <CardHeader>
            <CardTitle>Budget</CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6 space-y-3">
            <InfoRow
              label="Budget"
              value={legalService.formatCurrency(matter.budgetAmount, matter.budgetCurrency)}
            />
            <InfoRow
              label="Reserve"
              value={legalService.formatCurrency(matter.reserveAmount, matter.budgetCurrency)}
            />
          </CardContent>
        </Card>
      </div>

      {/* Tags */}
      {matter.tags?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {matter.tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1 bg-secondary-100 dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 rounded-full text-sm"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function TimelineTab({ matterId }: { matterId: string }) {
  const [events, setEvents] = useState<MatterEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newEvent, setNewEvent] = useState({
    eventType: 'NOTE',
    title: '',
    description: '',
    eventDate: '',
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    legalService
      .getEvents(matterId)
      .then((data) => { if (!cancelled) {setEvents(data);} })
      .catch((err) => {
        if (!cancelled) {toast.error(err instanceof Error ? err.message : 'Failed to load events');}
      })
      .finally(() => { if (!cancelled) {setLoading(false);} });
    return () => { cancelled = true; };
  }, [matterId]);

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    if (!newEvent.title.trim() || !newEvent.eventDate) {
      toast.error('Title and date are required');
      return;
    }
    try {
      setSubmitting(true);
      const created = await legalService.createEvent(matterId, {
        eventType: newEvent.eventType,
        title: newEvent.title.trim(),
        description: newEvent.description.trim() || undefined,
        eventDate: new Date(newEvent.eventDate).toISOString(),
      });
      setEvents((prev) => [created, ...prev]);
      setNewEvent({ eventType: 'NOTE', title: '', description: '', eventDate: '' });
      setShowForm(false);
      toast.success('Event added');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add event');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-secondary-600 dark:text-secondary-400">
        Loading events...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" onClick={() => setShowForm((v) => !v)}>
          <PlusIcon className="w-4 h-4 mr-2" />
          Add Event
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleAdd} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Event Type</label>
                  <select
                    value={newEvent.eventType}
                    onChange={(e) => setNewEvent((p) => ({ ...p, eventType: e.target.value }))}
                    className={cn(inputCls, 'mt-1')}
                  >
                    {EVENT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {EVENT_TYPE_LABELS[t] ?? t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <DateTimeInput
                    label="Date"
                    value={newEvent.eventDate}
                    onChange={(value) => setNewEvent((p) => ({ ...p, eventDate: value }))}
                    className="mt-1"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Title</label>
                  <input
                    type="text"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent((p) => ({ ...p, title: e.target.value }))}
                    className={cn(inputCls, 'mt-1')}
                    required
                    placeholder="Event title"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Description</label>
                  <textarea
                    value={newEvent.description}
                    onChange={(e) => setNewEvent((p) => ({ ...p, description: e.target.value }))}
                    rows={3}
                    className={cn(inputCls, 'mt-1')}
                    placeholder="Optional description"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Adding...' : 'Add Event'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {events.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-secondary-600 dark:text-secondary-400">
            No events recorded yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {events.map((ev) => (
            <Card key={ev.id}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="mt-1">
                    <ClockIcon className="w-5 h-5 text-secondary-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium px-2 py-0.5 bg-secondary-100 dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 rounded">
                        {EVENT_TYPE_LABELS[ev.eventType] ?? ev.eventType}
                      </span>
                      <span className="text-xs text-secondary-500 dark:text-secondary-400">
                        {legalService.formatDate(ev.eventDate)}
                      </span>
                    </div>
                    <p className="font-medium text-secondary-900 dark:text-white">{ev.title}</p>
                    {ev.description && (
                      <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                        {ev.description}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function FinancialTab({ matterId, matter }: { matterId: string; matter: LegalMatter }) {
  const [costs, setCosts] = useState<MatterCost[]>([]);
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newCost, setNewCost] = useState({
    category: 'OTHER_EXPENSES',
    description: '',
    amount: '',
    costDate: '',
    vendorName: '',
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([legalService.getCosts(matterId), legalService.getFinancialSummary(matterId)])
      .then(([costsData, summaryData]) => {
        if (cancelled) {return;}
        setCosts(costsData);
        setSummary(summaryData);
      })
      .catch((err) => {
        if (!cancelled)
          {toast.error(err instanceof Error ? err.message : 'Failed to load financial data');}
      })
      .finally(() => { if (!cancelled) {setLoading(false);} });
    return () => { cancelled = true; };
  }, [matterId]);

  const handleAddCost = async (e: FormEvent) => {
    e.preventDefault();
    if (!newCost.description.trim() || !newCost.amount || !newCost.costDate) {
      toast.error('Description, amount, and date are required');
      return;
    }
    try {
      setSubmitting(true);
      const created = await legalService.createCost(matterId, {
        category: newCost.category,
        description: newCost.description.trim(),
        amount: parseFloat(newCost.amount),
        costDate: new Date(newCost.costDate).toISOString(),
        vendorName: newCost.vendorName.trim() || undefined,
        currency: matter.budgetCurrency ?? 'USD',
      });
      setCosts((prev) => [created, ...prev]);
      setNewCost({ category: 'OTHER_EXPENSES', description: '', amount: '', costDate: '', vendorName: '' });
      setShowForm(false);
      toast.success('Cost entry added');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add cost');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (costId: string) => {
    try {
      await legalService.deleteCost(matterId, costId);
      setCosts((prev) => prev.filter((c) => c.id !== costId));
      toast.success('Cost entry deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete cost');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-secondary-600 dark:text-secondary-400">
        Loading financial data...
      </div>
    );
  }

  const utilPct = summary?.budgetUtilization;
  const utilColor =
    utilPct === null || utilPct === undefined
      ? 'bg-secondary-400'
      : utilPct >= 90
      ? 'bg-danger-500'
      : utilPct >= 70
      ? 'bg-warning-500'
      : 'bg-success-500';

  return (
    <div className="space-y-6">
      {/* Budget Overview */}
      {summary && (
        <Card>
          <CardHeader>
            <CardTitle>Budget Overview</CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6 space-y-4">
            {utilPct !== null && utilPct !== undefined && (
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-secondary-600 dark:text-secondary-400">
                    Budget Utilization
                  </span>
                  <span className="font-medium text-secondary-900 dark:text-white">
                    {utilPct.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-full h-3">
                  <div
                    className={cn('h-3 rounded-full transition-all', utilColor)}
                    style={{ width: `${Math.min(100, utilPct)}%` }}
                  />
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-secondary-500 dark:text-secondary-400">Budget</div>
                <div className="text-lg font-bold text-secondary-900 dark:text-white">
                  {legalService.formatCurrency(summary.budgetAmount, matter.budgetCurrency)}
                </div>
              </div>
              <div>
                <div className="text-xs text-secondary-500 dark:text-secondary-400">Actual</div>
                <div className="text-lg font-bold text-secondary-900 dark:text-white">
                  {legalService.formatCurrency(summary.totalCosts, matter.budgetCurrency)}
                </div>
              </div>
              <div>
                <div className="text-xs text-secondary-500 dark:text-secondary-400">Reserve</div>
                <div className="text-lg font-bold text-secondary-900 dark:text-white">
                  {legalService.formatCurrency(summary.reserveAmount, matter.budgetCurrency)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cost Entries */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Cost Entries</h3>
        <Button variant="outline" onClick={() => setShowForm((v) => !v)}>
          <PlusIcon className="w-4 h-4 mr-2" />
          Add Cost
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleAddCost} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Category</label>
                  <select
                    value={newCost.category}
                    onChange={(e) => setNewCost((p) => ({ ...p, category: e.target.value }))}
                    className={cn(inputCls, 'mt-1')}
                  >
                    {COST_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {COST_CATEGORY_LABELS[c] ?? c}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <DateInput
                    label="Date"
                    value={newCost.costDate}
                    onChange={(value) => setNewCost((p) => ({ ...p, costDate: value }))}
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <label className={labelCls}>Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newCost.amount}
                    onChange={(e) => setNewCost((p) => ({ ...p, amount: e.target.value }))}
                    className={cn(inputCls, 'mt-1')}
                    required
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className={labelCls}>Vendor</label>
                  <input
                    type="text"
                    value={newCost.vendorName}
                    onChange={(e) => setNewCost((p) => ({ ...p, vendorName: e.target.value }))}
                    className={cn(inputCls, 'mt-1')}
                    placeholder="Optional vendor name"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Description</label>
                  <input
                    type="text"
                    value={newCost.description}
                    onChange={(e) => setNewCost((p) => ({ ...p, description: e.target.value }))}
                    className={cn(inputCls, 'mt-1')}
                    required
                    placeholder="Cost description"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Adding...' : 'Add Cost'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {costs.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-secondary-600 dark:text-secondary-400">
            No cost entries yet.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-secondary-200 dark:border-secondary-700">
                    <th className="text-left px-4 py-3 text-secondary-600 dark:text-secondary-400 font-medium">Date</th>
                    <th className="text-left px-4 py-3 text-secondary-600 dark:text-secondary-400 font-medium">Category</th>
                    <th className="text-left px-4 py-3 text-secondary-600 dark:text-secondary-400 font-medium">Description</th>
                    <th className="text-left px-4 py-3 text-secondary-600 dark:text-secondary-400 font-medium">Vendor</th>
                    <th className="text-right px-4 py-3 text-secondary-600 dark:text-secondary-400 font-medium">Amount</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {costs.map((cost) => (
                    <tr
                      key={cost.id}
                      className="border-b border-secondary-100 dark:border-secondary-800 hover:bg-secondary-50 dark:hover:bg-secondary-800/50"
                    >
                      <td className="px-4 py-3 text-secondary-700 dark:text-secondary-300">
                        {legalService.formatDate(cost.costDate)}
                      </td>
                      <td className="px-4 py-3 text-secondary-700 dark:text-secondary-300">
                        {COST_CATEGORY_LABELS[cost.category] ?? cost.category}
                      </td>
                      <td className="px-4 py-3 text-secondary-900 dark:text-white">
                        {cost.description}
                      </td>
                      <td className="px-4 py-3 text-secondary-600 dark:text-secondary-400">
                        {cost.vendorName ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-medium text-secondary-900 dark:text-white">
                        {legalService.formatCurrency(cost.amount, cost.currency)}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleDelete(cost.id)}
                          className="p-1 rounded hover:bg-danger-100 dark:hover:bg-danger-900/20 text-secondary-400 hover:text-danger-600 transition-colors"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DocumentsTab({ matterId }: { matterId: string }) {
  const [documents, setDocuments] = useState<MatterDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newDoc, setNewDoc] = useState({ description: '', category: '', folder: '', privilege: 'NOT_PRIVILEGED' });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    legalService
      .getDocuments(matterId)
      .then((data) => { if (!cancelled) {setDocuments(data);} })
      .catch((err) => {
        if (!cancelled)
          {toast.error(err instanceof Error ? err.message : 'Failed to load documents');}
      })
      .finally(() => { if (!cancelled) {setLoading(false);} });
    return () => { cancelled = true; };
  }, [matterId]);

  const handleUpload = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    try {
      setSubmitting(true);
      const created = await legalService.uploadDocument(matterId, selectedFile, {
        description: newDoc.description.trim() || undefined,
        category: newDoc.category || undefined,
        folder: newDoc.folder.trim() || undefined,
        privilege: newDoc.privilege,
      });
      setDocuments((prev) => [created, ...prev]);
      setNewDoc({ description: '', category: '', folder: '', privilege: 'NOT_PRIVILEGED' });
      setSelectedFile(null);
      setShowForm(false);
      toast.success('Document uploaded');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload document');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (docId: string) => {
    try {
      await legalService.deleteDocument(matterId, docId);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
      toast.success('Document removed');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete document');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-secondary-600 dark:text-secondary-400">
        Loading documents...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" onClick={() => setShowForm((v) => !v)}>
          <PlusIcon className="w-4 h-4 mr-2" />
          Upload Document
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleUpload} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={labelCls}>File</label>
                  <input
                    type="file"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                    className={cn(inputCls, 'mt-1')}
                    required
                  />
                </div>
                <div>
                  <label className={labelCls}>Privilege Designation</label>
                  <select
                    value={newDoc.privilege}
                    onChange={(e) => setNewDoc((p) => ({ ...p, privilege: e.target.value }))}
                    className={cn(inputCls, 'mt-1')}
                  >
                    <option value="NOT_PRIVILEGED">Not Privileged</option>
                    <option value="ATTORNEY_CLIENT">Attorney-Client</option>
                    <option value="WORK_PRODUCT">Work Product</option>
                    <option value="JOINT_DEFENSE">Joint Defense</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Folder</label>
                  <input
                    type="text"
                    value={newDoc.folder}
                    onChange={(e) => setNewDoc((p) => ({ ...p, folder: e.target.value }))}
                    className={cn(inputCls, 'mt-1')}
                    placeholder="Optional folder name"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Description</label>
                  <input
                    type="text"
                    value={newDoc.description}
                    onChange={(e) => setNewDoc((p) => ({ ...p, description: e.target.value }))}
                    className={cn(inputCls, 'mt-1')}
                    placeholder="Optional description"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {documents.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-secondary-600 dark:text-secondary-400">
            No documents uploaded yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {documents.map((doc) => (
            <Card key={doc.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <DocumentTextIcon className="w-5 h-5 text-secondary-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-secondary-900 dark:text-white truncate">
                        {doc.fileName}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {doc.privilege && doc.privilege !== 'NOT_PRIVILEGED' && (
                          <span className="text-xs px-2 py-0.5 bg-warning-100 dark:bg-warning-900/20 text-warning-800 dark:text-warning-300 rounded-full flex items-center gap-1">
                            <LockClosedIcon className="w-3 h-3" />
                            {doc.privilege.replace(/_/g, ' ')}
                          </span>
                        )}
                        {doc.folder && (
                          <span className="text-xs text-secondary-500 dark:text-secondary-400 flex items-center gap-1">
                            <FolderIcon className="w-3 h-3" />
                            {doc.folder}
                          </span>
                        )}
                        <span className="text-xs text-secondary-500 dark:text-secondary-400">
                          {legalService.formatFileSize(doc.fileSize)}
                        </span>
                        <span className="text-xs text-secondary-500 dark:text-secondary-400">
                          {legalService.formatDate(doc.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(doc.id)}
                    className="p-1 rounded hover:bg-danger-100 dark:hover:bg-danger-900/20 text-secondary-400 hover:text-danger-600 transition-colors flex-shrink-0"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function HoldsTab({ matter }: { matter: LegalMatter }) {
  const holds = matter.holds ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Link to={`/legal/holds/create?matterId=${matter.id}`}>
          <Button variant="outline">
            <PlusIcon className="w-4 h-4 mr-2" />
            Create Hold
          </Button>
        </Link>
      </div>

      {holds.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-secondary-600 dark:text-secondary-400">
            No legal holds linked to this matter.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {holds.map((hold) => {
            const holdCfg = legalService.getHoldStatusConfig(hold.status);
            return (
              <Card key={hold.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <Link
                        to={`/legal/holds/${hold.id}`}
                        className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        {hold.holdName}
                      </Link>
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className={cn(
                            'text-xs px-2 py-0.5 rounded-full',
                            STATUS_BADGE_COLORS[holdCfg.color]
                          )}
                        >
                          {holdCfg.label}
                        </span>
                        {hold._count && (
                          <span className="text-xs text-secondary-500 dark:text-secondary-400">
                            {hold._count.custodians} custodian
                            {hold._count.custodians !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <Link to={`/legal/holds/${hold.id}`}>
                      <Button variant="outline" size="sm">
                        View
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Status Transition Modal ────────────────────────────────────────────────────

function StatusModal({
  transitions,
  onClose,
  onSubmit,
}: {
  transitions: string[];
  onClose: () => void;
  onSubmit: (status: string, reason: string) => void;
}) {
  const [selected, setSelected] = useState(transitions[0] ?? '');
  const [reason, setReason] = useState('');
  const needsReason = ['ON_HOLD', 'CLOSED'].includes(selected);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-secondary-900 rounded-xl p-6 w-full max-w-md shadow-xl mx-4">
        <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
          Update Status
        </h3>
        <div className="space-y-4">
          <div>
            <label className={labelCls}>New Status</label>
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className={cn(inputCls, 'mt-1')}
            >
              {transitions.map((t) => (
                <option key={t} value={t}>
                  {MATTER_STATUS_LABELS[t] ?? t}
                </option>
              ))}
            </select>
          </div>
          {needsReason && (
            <div>
              <label className={labelCls}>Reason</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className={cn(inputCls, 'mt-1')}
                placeholder="Provide a reason for this status change..."
              />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit(selected, reason)}
            disabled={needsReason && !reason.trim()}
          >
            Update Status
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const TABS: { key: TabKey; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'overview', label: 'Overview', icon: DocumentTextIcon },
  { key: 'timeline', label: 'Timeline', icon: ClockIcon },
  { key: 'financial', label: 'Financial', icon: CurrencyDollarIcon },
  { key: 'documents', label: 'Documents', icon: FolderIcon },
  { key: 'holds', label: 'Holds', icon: LockClosedIcon },
];

export default function MatterDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [matter, setMatter] = useState<LegalMatter | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [showStatusModal, setShowStatusModal] = useState(false);

  const fetchMatter = useCallback(async () => {
    if (!id) {return;}
    try {
      setLoading(true);
      const data = await legalService.getMatter(id);
      setMatter(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load matter');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchMatter();
  }, [fetchMatter]);

  const handleStatusChange = async (status: string, reason: string) => {
    if (!id) {return;}
    try {
      const updated = await legalService.updateMatterStatus(id, status, reason || undefined);
      setMatter(updated);
      setShowStatusModal(false);
      toast.success('Status updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-center text-secondary-600 dark:text-secondary-400 py-12">
          Loading matter...
        </div>
      </div>
    );
  }

  if (error || !matter) {
    return (
      <div className="p-4 sm:p-6">
        <Card>
          <CardContent className="p-12 text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-danger-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
              Error Loading Matter
            </h3>
            <p className="text-secondary-600 dark:text-secondary-400 mb-4">
              {error ?? 'Matter not found'}
            </p>
            <Button onClick={() => navigate('/legal/matters')} variant="outline">
              Back to Matters
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusCfg = legalService.getStatusConfig(matter.status);
  const priorityCfg = legalService.getPriorityConfig(matter.priority);
  const transitions = legalService.getValidTransitions(matter.status);

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <button
              onClick={() => navigate('/legal/matters')}
              className="mt-1 p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
            </button>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-mono text-secondary-500 dark:text-secondary-400">
                  {matter.matterNumber}
                </span>
              </div>
              <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
                {matter.title}
              </h1>
              <div className="flex flex-wrap gap-2 mt-2">
                <span
                  className={cn(
                    'px-3 py-1 text-xs font-medium rounded-full',
                    STATUS_BADGE_COLORS[statusCfg.color]
                  )}
                >
                  {statusCfg.label}
                </span>
                <span
                  className={cn(
                    'px-3 py-1 text-xs font-medium rounded-full',
                    STATUS_BADGE_COLORS[priorityCfg.color]
                  )}
                >
                  {priorityCfg.label} Priority
                </span>
                <span className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300 rounded-full">
                  {MATTER_TYPE_LABELS[matter.type] ?? matter.type}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {transitions.length > 0 && (
              <Button variant="outline" onClick={() => setShowStatusModal(true)}>
                Update Status
              </Button>
            )}
            <Link to={`/legal/matters/${matter.id}/edit`}>
              <Button variant="outline">
                <PencilIcon className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </Link>
          </div>
        </div>
      </motion.div>

      {/* Tabs */}
      <div className="border-b border-secondary-200 dark:border-secondary-700">
        <nav className="flex gap-0 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                  activeTab === tab.key
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white hover:border-secondary-300'
                )}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && <OverviewTab matter={matter} />}
        {activeTab === 'timeline' && <TimelineTab matterId={matter.id} />}
        {activeTab === 'financial' && <FinancialTab matterId={matter.id} matter={matter} />}
        {activeTab === 'documents' && <DocumentsTab matterId={matter.id} />}
        {activeTab === 'holds' && <HoldsTab matter={matter} />}
      </div>

      {/* Status Modal */}
      {showStatusModal && (
        <StatusModal
          transitions={transitions}
          onClose={() => setShowStatusModal(false)}
          onSubmit={handleStatusChange}
        />
      )}
    </div>
  );
}
